// One scene of the draft editor (E31-S6-T2; mockups/content-edit.html, "Scenes, in order"): a card that
// opens to the scene's still with its highlight box drawn over it, Replace image, the box as four
// numbers (or none), the narration with its word count and spoken time, and the guide page beside the
// title's pages. The scene's kind, heading, statement and zoom are not edited here; they are carried
// through as they were. Every error is tied to its field.
import type { Scene } from "@waypoint/contracts";
import { Button, cn, focusRing, Icon } from "@waypoint/surface";
import type { ChangeEvent } from "react";
import { t } from "../../i18n/t.js";
import { FieldError, Label, StatusChip, TextArea } from "../../ui/form.js";
import {
  hasStill,
  MIB,
  type Box,
  type HeldStill,
  type SceneDraft,
  type SceneProblem,
} from "../../versions/edit.js";
import { secondsFor, wordsOf } from "../../versions/model.js";

export const FIELD =
  "w-full min-h-8.5 rounded-8 border border-wp-line-strong bg-wp-bg/58 px-2.5 py-1.5 font-sans text-13 text-wp-text focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-wp-accent aria-invalid:border-stop-soft/62";
const HINT = "mt-1 text-12 text-wp-text-3";
const MONO = "font-mono text-12 font-semibold text-wp-text-3";

export const mb = (bytes: number) => t("edit.size.mb", { n: (bytes / MIB).toFixed(1) });
const num = (n: number) => n.toLocaleString("en-GB");

/** What the scene's fields say is wrong, in words, by field. */
export interface SceneMessages {
  still?: string | undefined;
  narration?: string | undefined;
  pages?: string | undefined;
  box?: string | undefined;
}

export function sceneMessages(
  draft: SceneDraft,
  problems: readonly SceneProblem[],
  still: HeldStill | undefined,
  guidePages: string | undefined,
): SceneMessages {
  const out: SceneMessages = {};
  for (const p of problems) {
    switch (p.field) {
      case "narration":
        out.narration =
          p.kind === "empty"
            ? t("edit.narration.empty")
            : t("edit.narration.long", { n: num(draft.narration.trim().length) });
        break;
      case "pages":
        out.pages =
          p.kind === "empty"
            ? t("edit.pages.empty")
            : p.kind === "format"
              ? t("edit.pages.format")
              : t("edit.pages.outside", { cited: draft.pages.trim(), pages: guidePages ?? "" });
        break;
      case "box": {
        const box = draft.box;
        out.box =
          p.kind === "whole" || box === null || box === undefined || still === undefined
            ? t("edit.box.whole")
            : p.kind === "right"
              ? t("edit.box.right", { sum: num(box.x + box.w), w: num(still.width) })
              : t("edit.box.bottom", { sum: num(box.y + box.h), h: num(still.height) });
        break;
      }
    }
  }
  return out;
}

function headingOf(scene: Scene): string {
  if (scene.kind === "title") return scene.text;
  if (scene.kind === "still" && scene.kicker !== undefined) return scene.kicker;
  return t(`review.scene.${scene.kind}`);
}

const pct = (v: number, of: number) => `${String(Math.max(0, Math.min(100, (v / of) * 100)))}%`;

/** The still, with the box drawn over it in the still's own pixels. */
function StillView({
  n,
  still,
  url,
  box,
  bad,
  thumb,
}: {
  n: number;
  still: HeldStill | undefined;
  url: string | undefined;
  box: Box | null | undefined;
  bad: boolean;
  thumb: boolean;
}) {
  const shown = box !== null && box !== undefined && still !== undefined;
  const label = shown
    ? t("edit.still.alt", { n, x: box.x, y: box.y, w: box.w, h: box.h })
    : t("edit.still.altNoBox", { n });
  return (
    <div
      data-still={n}
      {...(thumb ? { "aria-hidden": true } : { role: "img", "aria-label": label })}
      className={cn(
        "relative aspect-video w-full overflow-hidden border border-wp-line-strong bg-wp-bg/58",
        thumb ? "rounded-4" : "rounded-8",
      )}
    >
      {url === undefined ? null : (
        <img src={url} alt="" className="absolute inset-0 block size-full object-contain" />
      )}
      {shown ? (
        <div
          data-box
          aria-hidden
          className={cn(
            "pointer-events-none absolute rounded-4",
            thumb ? "border-[1.5px]" : "border-2",
            bad
              ? "border-dashed border-stop-soft"
              : cn("border-wp-accent", thumb ? "" : "shadow-wp-glow"),
          )}
          style={{
            left: pct(box.x, still.width),
            top: pct(box.y, still.height),
            width: pct(box.w, still.width),
            height: pct(box.h, still.height),
          }}
        />
      ) : null}
    </div>
  );
}

const BOX_FIELDS = [
  ["x", "edit.box.x"],
  ["y", "edit.box.y"],
  ["w", "edit.box.w"],
  ["h", "edit.box.h"],
] as const;

export interface SceneEditorProps {
  readonly index: number;
  readonly scene: Scene;
  readonly draft: SceneDraft;
  readonly still: HeldStill | undefined;
  readonly stillUrl: string | undefined;
  /** The still was replaced in this editing session and is not saved yet. */
  readonly replaced: boolean;
  /** A chosen file that was refused: its name. */
  readonly refusedFile?: string | undefined;
  readonly reading: boolean;
  readonly messages: SceneMessages;
  readonly guidePages: string | undefined;
  readonly changed: boolean;
  readonly open: boolean;
  readonly onToggle: () => void;
  readonly onChange: (draft: SceneDraft) => void;
  readonly onFile: (file: File) => void;
  readonly onAddBox: () => void;
  readonly onNoBox: () => void;
}

export function SceneEditor(props: SceneEditorProps) {
  const { index, scene, draft, still, messages, open } = props;
  const n = index + 1;
  const id = (part: string) => `s${String(index)}-${part}`;
  const count = Object.values(messages).filter((m) => m !== undefined).length;
  const words = wordsOf(draft.narration);
  const box = draft.box;
  const editsStill = hasStill(scene);

  const setBox = (k: keyof Box) => (e: ChangeEvent<HTMLInputElement>) => {
    if (box === null || box === undefined) return;
    const v = e.target.value === "" ? Number.NaN : Number(e.target.value);
    props.onChange({ ...draft, box: { ...box, [k]: v } });
  };

  return (
    <li
      id={`scene${String(index)}`}
      className={cn(
        "rounded-12 border bg-wp-panel/74",
        count > 0 ? "border-stop/48" : "border-wp-line",
      )}
    >
      <button
        type="button"
        id={id("toggle")}
        aria-expanded={open}
        aria-controls={id("body")}
        onClick={props.onToggle}
        className={cn(
          "grid w-full cursor-pointer grid-cols-[112px_minmax(0,1fr)] items-center gap-x-3 gap-y-2 rounded-12 border-0 bg-transparent px-3 py-2.5 text-left font-sans text-wp-text hover:bg-br-sky/9 min-[721px]:grid-cols-[128px_minmax(0,1fr)_auto]",
          focusRing,
        )}
      >
        {editsStill ? (
          <StillView
            n={n}
            still={still}
            url={props.stillUrl}
            box={box}
            bad={messages.box !== undefined}
            thumb
          />
        ) : (
          <span className="grid aspect-video place-items-center rounded-4 border border-wp-line-strong bg-wp-bg/58 text-11 text-wp-text-3">
            {t(`review.scene.${scene.kind}`)}
          </span>
        )}
        <span className="col-span-full grid min-w-0 gap-1 min-[721px]:col-span-1">
          <span className="flex flex-wrap items-center gap-x-2.5 gap-y-1.5">
            <span className={MONO}>{t("edit.scene", { n })}</span>
            <span className="text-14 font-semibold">{headingOf(scene)}</span>
            <span
              className={cn(
                "rounded-pill border px-1.75 py-0.5 font-mono text-11 font-semibold whitespace-nowrap",
                messages.pages === undefined
                  ? "border-wp-accent/44 bg-wp-accent/13 text-wp-accent"
                  : "border-stop/48 bg-stop/14 text-stop-soft",
              )}
            >
              {draft.pages.trim() === "" ? t("edit.scene.noPage") : draft.pages}
            </span>
          </span>
          <span className="line-clamp-2 text-13 text-wp-text-2 min-[721px]:line-clamp-1">
            {draft.narration}
          </span>
        </span>
        <span className="col-start-2 row-start-1 flex flex-wrap items-center justify-end gap-1.5 self-start min-[721px]:col-start-3 min-[721px]:self-center">
          {count > 0 ? (
            <StatusChip tone="stop">{t("edit.scene.toFix", { n: count })}</StatusChip>
          ) : null}
          {props.changed ? <StatusChip tone="open">{t("edit.scene.changed")}</StatusChip> : null}
          <Icon
            name="chevron"
            size={16}
            className={cn("text-wp-text-3 transition-transform", open ? "rotate-180" : "")}
          />
        </span>
      </button>

      <div
        id={id("body")}
        role="region"
        aria-labelledby={id("toggle")}
        hidden={!open}
        className="grid gap-4.5 border-t border-wp-line px-3.5 pt-3.5 pb-4 min-[901px]:grid-cols-[minmax(0,1.05fr)_minmax(0,1fr)]"
      >
        {open ? (
          <>
            {editsStill ? (
              <div className="min-w-0">
                <StillView
                  n={n}
                  still={still}
                  url={props.stillUrl}
                  box={box}
                  bad={messages.box !== undefined}
                  thumb={false}
                />
                <div className="mt-2 grid gap-1.5">
                  <div
                    id={id("fileinfo")}
                    className="flex flex-wrap items-center gap-x-2.5 gap-y-1.5 text-12 text-wp-text-3"
                  >
                    {still === undefined ? null : (
                      <>
                        <b className="font-semibold text-wp-text-2">{still.fileName ?? still.id}</b>
                        <span>
                          {t("edit.still.info", {
                            size: mb(still.bytes.byteLength),
                            w: num(still.width),
                            h: num(still.height),
                          })}
                        </span>
                      </>
                    )}
                    {props.replaced ? (
                      <StatusChip tone="open">{t("edit.still.new")}</StatusChip>
                    ) : null}
                    {props.refusedFile === undefined ? null : (
                      <StatusChip tone="stop">
                        {t("edit.still.notUsed", { name: props.refusedFile })}
                      </StatusChip>
                    )}
                    {props.reading ? <span role="status">{t("edit.still.reading")}</span> : null}
                  </div>
                  <div className="flex flex-wrap items-center gap-x-2.5 gap-y-1.5 text-12 text-wp-text-3">
                    <label
                      htmlFor={id("file")}
                      className="relative inline-flex h-7 cursor-pointer items-center rounded-8 border border-wp-line-strong px-2.5 text-12 font-semibold text-wp-text hover:bg-br-sky/9 has-focus-visible:outline-2 has-focus-visible:outline-offset-2 has-focus-visible:outline-wp-accent"
                    >
                      {t("edit.still.replace")}
                      <input
                        id={id("file")}
                        type="file"
                        accept="image/png,image/jpeg"
                        aria-describedby={`${id("fileinfo")} ${id("filehint")} ${id("fileerr")}`}
                        aria-invalid={messages.still !== undefined}
                        className="absolute inset-0 w-full cursor-pointer opacity-0"
                        onChange={(e) => {
                          const file = e.target.files?.[0];
                          if (file !== undefined) props.onFile(file);
                          e.target.value = "";
                        }}
                      />
                    </label>
                    <span id={id("filehint")}>{t("edit.still.hint")}</span>
                  </div>
                  <FieldError id={id("fileerr")}>{messages.still}</FieldError>
                </div>

                {scene.kind === "still" ? (
                  <fieldset
                    className="m-0 mt-3 min-w-0 border-0 p-0"
                    aria-describedby={`${id("boxhint")} ${id("boxerr")}`}
                  >
                    <legend className="mb-1 p-0 text-12 font-semibold text-wp-text-2">
                      {t("edit.box.legend")}
                    </legend>
                    {box === null || box === undefined ? (
                      <div className="mt-1 flex flex-wrap items-center justify-between gap-x-2.5 gap-y-1.5">
                        <span id={id("boxhint")} className="text-12 text-wp-text-3">
                          {t("edit.box.none")}
                        </span>
                        <Button compact id={id("add")} onClick={props.onAddBox}>
                          {t("edit.box.add")}
                        </Button>
                      </div>
                    ) : (
                      <>
                        <div className="grid grid-cols-2 gap-2 min-[721px]:grid-cols-4">
                          {BOX_FIELDS.map(([k, key]) => (
                            <label key={k} className="grid gap-0.75 text-12 text-wp-text-3">
                              {t(key)}
                              <input
                                id={id(k)}
                                type="number"
                                inputMode="numeric"
                                step={1}
                                min={k === "w" || k === "h" ? 1 : 0}
                                value={Number.isNaN(box[k]) ? "" : String(box[k])}
                                onChange={setBox(k)}
                                aria-invalid={messages.box !== undefined}
                                className={cn(FIELD, "font-mono")}
                              />
                            </label>
                          ))}
                        </div>
                        <div className="mt-1 flex flex-wrap items-center justify-between gap-x-2.5 gap-y-1.5">
                          <span id={id("boxhint")} className="text-12 text-wp-text-3">
                            {still === undefined
                              ? null
                              : t("edit.box.hint", { w: num(still.width), h: num(still.height) })}
                          </span>
                          <Button compact variant="quiet" id={id("nobox")} onClick={props.onNoBox}>
                            {t("edit.box.remove")}
                          </Button>
                        </div>
                      </>
                    )}
                    <FieldError id={id("boxerr")}>{messages.box}</FieldError>
                  </fieldset>
                ) : null}
              </div>
            ) : null}

            <div className="grid min-w-0 content-start gap-3.5">
              <div>
                <Label htmlFor={id("narr")}>{t("edit.narration")}</Label>
                <TextArea
                  id={id("narr")}
                  value={draft.narration}
                  aria-invalid={messages.narration !== undefined}
                  aria-describedby={`${id("count")} ${id("narrerr")}`}
                  onChange={(e) => {
                    props.onChange({ ...draft, narration: e.target.value });
                  }}
                  className="min-h-37.5 text-14"
                />
                <div id={id("count")} className={HINT}>
                  {t("review.scene.words", { words, seconds: secondsFor(words) })}
                </div>
                <FieldError id={id("narrerr")}>{messages.narration}</FieldError>
              </div>
              <div>
                <Label htmlFor={id("pages")}>{t("edit.pages")}</Label>
                <div className="grid items-center gap-1 min-[721px]:grid-cols-[minmax(0,9rem)_minmax(0,1fr)] min-[721px]:gap-2.5">
                  <input
                    id={id("pages")}
                    value={draft.pages}
                    autoComplete="off"
                    spellCheck={false}
                    aria-invalid={messages.pages !== undefined}
                    aria-describedby={`${id("pageshint")} ${id("pageserr")}`}
                    onChange={(e) => {
                      props.onChange({ ...draft, pages: e.target.value });
                    }}
                    className={FIELD}
                  />
                  {props.guidePages === undefined ? (
                    <span id={id("pageshint")} />
                  ) : (
                    <p id={id("pageshint")} className="m-0 text-12 text-wp-text-3">
                      {t("edit.pages.guide", { pages: props.guidePages })}
                    </p>
                  )}
                </div>
                <FieldError id={id("pageserr")}>{messages.pages}</FieldError>
              </div>
            </div>
          </>
        ) : null}
      </div>
    </li>
  );
}
